import ClusterData from '../ClusterData';
import Constants from '../Constants';
import ImageRegistry from '../ImageRegistry';
import RenderingState from '../RenderingState';
import TopicVizScaling from '../TopicVizScaling';
import { getTruncatedString } from '../Utils';
import DocumentCard from '../VirtualDom/drawElements/DocumentCard';
import DocumentPoint from '../VirtualDom/drawElements/DocumentPoint';
import Label from '../VirtualDom/drawElements/Label';
import VirtualDom from '../VirtualDom/VirtualDom';
import VirtualDomLayer from '../VirtualDom/VirtualDomLayer';

export default function createDocumentLevel(
  documentDataPoints,
  virtualDom: VirtualDom,
  scaling: TopicVizScaling,
  imageRegistry: ImageRegistry,
  renderingState: RenderingState,
  clusterData: ClusterData,
) {
  createDocumentPoints(documentDataPoints, virtualDom, scaling);
  createDocumentCards(
    virtualDom,
    scaling,
    imageRegistry,
    renderingState,
    clusterData,
  );
}

const createDocumentPoints = (
  documentDataPoints,
  virtualDom: VirtualDom,
  scaling: TopicVizScaling,
) => {
  const documentPoints = [];
  const keywordLabels = [];

  const scaleFactor = scaling.getScaleFactor();

  for (const dataPoint of documentDataPoints.filter(
    dataPoint => dataPoint['meta']['source'] !== 'word',
  )) {
    const x = scaling.xScale(dataPoint.getX());
    const y = scaling.yScale(dataPoint.getY());
    // outer boundary
    const widthDocumentPoint = scaleFactor * Constants.documentPoint.width;
    const heightDocumentPoint = scaleFactor * Constants.documentPoint.width;

    const documentPoint = new DocumentPoint({
      x: x,
      y: y,
      width: widthDocumentPoint,
      height: heightDocumentPoint,
      color: scaling.colorScale(dataPoint.getClusterId()),
    });

    documentPoint.set('fill', true);
    documentPoint.set('documentId', dataPoint.getId());
    documentPoint.set('clusterId', dataPoint.getClusterId());
    documentPoints.push(documentPoint);

    if (
      Math.random() >= Constants.keywordLabels.sampleRate &&
      dataPoint.meta.words[0]
    ) {
      // let keyword = dataPoint.meta.words[0];
      // if (dataPoint.meta.source === 'keyword') {
      //   keyword = dataPoint.text;
      // }

      const keywordLabel = new Label(
        x - widthDocumentPoint * 0.25,
        y + heightDocumentPoint * 0.95,
        getTruncatedString(
          dataPoint.meta.words[0].toUpperCase(),
          Constants.keywordLabels.maxCharacterLength,
        ),
        scaling.getScaleFactor() * Constants.keywordLabels.fontSize,
        widthDocumentPoint * 1.5,
        () => null,
      );

      keywordLabels.push(keywordLabel);
    }
  }

  const selection = new VirtualDomLayer({
    name: 'documentPoints',
    elements: documentPoints,
    keyFramesOpacity: [
      [Constants.thresholds.documentPoints[0] - 0.2, 0],
      [Constants.thresholds.documentPoints[0], 1],
      [Constants.thresholds.documentPoints[1], 1],
      [Constants.thresholds.documentPoints[1] + 0.2, 0],
    ],
  });

  const keywordLabelLayer = new VirtualDomLayer({
    name: 'keywordLabels',
    elements: keywordLabels,
    keyFramesOpacity: [
      [Constants.thresholds.keywordLabel[0] - 0.2, 0],
      [Constants.thresholds.keywordLabel[0], 1],
      [Constants.thresholds.keywordLabel[1], 1],
      [Constants.thresholds.keywordLabel[1] + 0.2, 0],
    ],
  });

  virtualDom.registerLayer(selection);
  virtualDom.registerLayer(keywordLabelLayer);
};

const createDocumentCards = (
  virtualDom: VirtualDom,
  scaling: TopicVizScaling,
  imageRegistry: ImageRegistry,
  renderingState: RenderingState,
  clusterData: ClusterData,
) => {
  const level3Selection = new VirtualDomLayer({ name: '' });
  level3Selection.addIndex('url', element => {
    const urls = [];
    for (const child of element.getChildren()) {
      const url = child.getAttribute('url');
      if (url) {
        urls.push(url);
      }
    }

    return urls;
  });

  const documentCards = [];

  for (const point of virtualDom.getLayer('documentPoints').getElements()) {
    const documentId = point.getAttribute('documentId');
    const documentPoint = clusterData.getDocumentPointById(documentId);
    const documentUrls = documentPoint
      .getUrls()
      .slice(0, Constants.documentPoint.countImagePreview);

    const documentCard = new DocumentCard({
      documentId: documentId,
      x: point.get('x'),
      y: point.get('y'),
      maxImageWidth:
        scaling.getScaleFactor() * Constants.documentPoint.pictureWidth * 2,
      maxImageHeight:
        scaling.getScaleFactor() * Constants.documentPoint.pictureHeight * 2,
      fontSize: scaling.getScaleFactor() * Constants.documentPoint.fontSize,
      text: documentPoint.getTitle(),
      urls: documentUrls,
      borderBottom:
        scaling.getScaleFactor() * Constants.documentPoint.borderWidthBottom,
      drawBorder: !point.get('fill'),
      borderColor: point.get('color'),
      onRequestDraw: () => renderingState.setDirtyBit(true),
      imageRegistry: imageRegistry,
    });

    for (const url of documentUrls) {
      imageRegistry.register(url, documentCard);
    }

    documentCards.push(documentCard);
  }

  const documentCardSection = new VirtualDomLayer({
    name: 'documentCards',
    elements: documentCards,
    keyFramesOpacity: [
      [Constants.thresholds.documentCard[0] - 0.2, 0],
      [Constants.thresholds.documentCard[0], 1],
      [100000, 1],
    ],
  });

  virtualDom.registerLayer(documentCardSection);
};
